import { useTranslation } from 'react-i18next';
import type { RuleArticle, DraftRuleArticle } from '@proref/types';
import type { CreateRuleArticleInput } from '@proref/schemas';

// The editable form-shape for one rule article. key_points is edited as a newline-joined string
// (one bullet per line) — far simpler than a nested field-array, and it's the same representation
// the add/edit modal and the AI-wizard review cards both use.
export interface EditableRule {
  article_number: string;
  title: string;
  summary: string;
  key_points: string; // one point per line
}

export const EMPTY_RULE: EditableRule = { article_number: '', title: '', summary: '', key_points: '' };

export function fromDraft(d: DraftRuleArticle): EditableRule {
  return {
    article_number: d.article_number,
    title: d.title,
    summary: d.summary,
    key_points: d.key_points.join('\n'),
  };
}

export function fromRuleArticle(a: RuleArticle): EditableRule {
  return fromDraft(a);
}

// Convert back to the API's create shape — split key_points into non-empty trimmed lines.
export function toInput(r: EditableRule): CreateRuleArticleInput {
  return {
    article_number: r.article_number.trim(),
    title: r.title.trim(),
    summary: r.summary.trim(),
    key_points: r.key_points
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean),
  };
}

// The three required text fields are present + non-empty (coarse client check; the server's Zod
// schema is the real gate and its errors surface via notifyError).
export function isRuleValid(r: EditableRule): boolean {
  return Boolean(r.article_number.trim() && r.title.trim() && r.summary.trim());
}

const inputClass =
  'w-full rounded-sm border border-line-2 bg-surface px-3 py-2 text-[13px] text-ink outline-none placeholder:text-muted focus:border-accent';
const labelClass = 'text-[11px] font-medium uppercase tracking-wider text-muted';

interface RuleArticleFieldsProps {
  value: EditableRule;
  onChange: (next: EditableRule) => void;
  // Unique prefix so <label htmlFor> ids don't collide when many editors render at once (wizard).
  idPrefix: string;
}

// The shared field set for one rule article. Presentational + controlled — the parent owns state.
export function RuleArticleFields({ value, onChange, idPrefix }: RuleArticleFieldsProps) {
  const { t } = useTranslation();
  function set<K extends keyof EditableRule>(key: K, v: EditableRule[K]) {
    onChange({ ...value, [key]: v });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={`${idPrefix}-num`} className={labelClass}>
            {t('admin.rulebooks.fields.numberLabel')}
          </label>
          <input
            id={`${idPrefix}-num`}
            value={value.article_number}
            onChange={(e) => set('article_number', e.target.value)}
            placeholder={t('admin.rulebooks.fields.numberPlaceholder')}
            className={inputClass + ' font-mono'}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${idPrefix}-title`} className={labelClass}>
            {t('admin.rulebooks.fields.titleLabel')}
          </label>
          <input
            id={`${idPrefix}-title`}
            value={value.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder={t('admin.rulebooks.fields.titlePlaceholder')}
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={`${idPrefix}-summary`} className={labelClass}>
          {t('admin.rulebooks.fields.summaryLabel')}
        </label>
        <textarea
          id={`${idPrefix}-summary`}
          value={value.summary}
          onChange={(e) => set('summary', e.target.value)}
          rows={2}
          placeholder={t('admin.rulebooks.fields.summaryPlaceholder')}
          className={inputClass + ' resize-y leading-relaxed'}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={`${idPrefix}-points`} className={labelClass}>
          {t('admin.rulebooks.fields.keyPointsLabel')}{' '}
          <span className="normal-case text-muted/70">{t('admin.rulebooks.fields.keyPointsHint')}</span>
        </label>
        <textarea
          id={`${idPrefix}-points`}
          value={value.key_points}
          onChange={(e) => set('key_points', e.target.value)}
          rows={3}
          placeholder={t('admin.rulebooks.fields.keyPointsPlaceholder')}
          className={inputClass + ' resize-y leading-relaxed'}
        />
      </div>
    </div>
  );
}
